import React, { useEffect, useMemo, useState } from 'react'
import TitleCard from '../components/TitleCard'
import { ConfirmModal, AlertModal } from '../components/Modal'

const TITLE_MAX = 80
const BODY_MAX = 240
const EMAIL_SUBJECT_MAX = 120
const EMAIL_BODY_MAX = 4000

function authHeaders() {
  const token = localStorage.getItem('cut-catering-token')
  return token
    ? { 'Content-Type': 'application/json', 'x-auth-token': token }
    : { 'Content-Type': 'application/json' }
}

function PushPreview({ title, body }) {
  const now = new Date()
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return (
    <div className="push-preview">
      <div className="push-preview-shell">
        <div className="push-preview-icon">CC</div>
        <div className="push-preview-body">
          <div className="push-preview-row">
            <span className="push-preview-app">CUT Catering</span>
            <span className="push-preview-time">
              {hh}:{mm}
            </span>
          </div>
          <div className="push-preview-title">
            {title || <span className="push-preview-empty">Titolo notifica</span>}
          </div>
          <div className="push-preview-text">
            {body || <span className="push-preview-empty">Anteprima del messaggio…</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmailPreview({ subject, body, fromName }) {
  return (
    <div className="email-preview-box">
      <div className="email-preview-head">
        <div>
          <b>Da:</b> {fromName || 'CUT Catering'} &lt;cutcatering@soolutions.io&gt;
        </div>
        <div>
          <b>Oggetto:</b>{' '}
          {subject ? subject : <i style={{ color: '#aaa' }}>Oggetto…</i>}
        </div>
      </div>
      <div className="email-preview-body-wrap">
        <div className="email-preview-card">
          <div className="email-preview-card-header">CUT Catering</div>
          <div className="email-preview-card-body">
            <h3>{subject || 'Oggetto…'}</h3>
            {body ? (
              body
                .split(/\n{2,}/)
                .map((p, i) => <p key={i}>{p}</p>)
            ) : (
              <p style={{ color: '#aaa' }}>
                Il corpo del messaggio apparirà qui…
              </p>
            )}
            <p style={{ color: '#888', fontSize: 13, marginTop: 18 }}>
              — Il team di CUT Catering
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function Broadcast() {
  const [pushTitle, setPushTitle] = useState('')
  const [pushBody, setPushBody] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')

  const [recipients, setRecipients] = useState({
    pushCount: null,
    emailCount: null,
    pushConfigured: true,
    emailConfigured: true,
  })

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [resultAlert, setResultAlert] = useState(null) // {title, message}

  useEffect(() => {
    fetch('/api/admin/notify/broadcast/recipients', { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) setRecipients(data)
      })
      .catch(() => {})
  }, [])

  // Riempi automaticamente i campi email con i valori della push se l'utente
  // attiva l'email senza aver ancora compilato. Riduce il lavoro per i cuochi.
  useEffect(() => {
    if (!sendEmail) return
    if (!emailSubject && pushTitle) setEmailSubject(pushTitle)
    if (!emailBody && pushBody) setEmailBody(pushBody)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmail])

  const canSubmit = useMemo(() => {
    if (!pushTitle.trim() || !pushBody.trim()) return false
    if (sendEmail && (!emailSubject.trim() || !emailBody.trim())) return false
    return true
  }, [pushTitle, pushBody, sendEmail, emailSubject, emailBody])

  const onClickInvia = () => {
    setError('')
    if (!canSubmit) {
      setError('Compila tutti i campi obbligatori prima di inviare.')
      return
    }
    setConfirmOpen(true)
  }

  const onConfirmSend = async () => {
    setConfirmOpen(false)
    setSending(true)
    setError('')
    try {
      const r = await fetch('/api/admin/notify/broadcast', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: pushTitle.trim(),
          body: pushBody.trim(),
          sendEmail,
          emailSubject: sendEmail ? emailSubject.trim() : undefined,
          emailBody: sendEmail ? emailBody.trim() : undefined,
        }),
      })
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        setError(t || `Errore (${r.status}). Riprova più tardi.`)
        setSending(false)
        return
      }
      const data = await r.json()
      const lines = []
      lines.push(
        `Notifica push: inviata a ${data.push.sent} dispositivi su ${data.push.recipients} destinatari.`,
      )
      if (data.push.failed) lines.push(`Push fallite: ${data.push.failed}.`)
      if (data.push.pruned)
        lines.push(`Subscription scadute rimosse: ${data.push.pruned}.`)
      if (sendEmail) {
        lines.push(
          `Email: ${data.email.sent}/${data.email.recipients} consegnate al provider.`,
        )
        if (data.email.failed) lines.push(`Email fallite: ${data.email.failed}.`)
      }
      setResultAlert({ title: 'Broadcast inviato', message: lines.join(' ') })
      setPushTitle('')
      setPushBody('')
      setEmailSubject('')
      setEmailBody('')
      setSendEmail(false)
      // Ricarica i conteggi (potrebbero essere cambiati per via dei pruned)
      try {
        const r2 = await fetch('/api/admin/notify/broadcast/recipients', {
          headers: authHeaders(),
        })
        if (r2.ok) setRecipients(await r2.json())
      } catch (e) {
        /* silent */
      }
    } catch (e) {
      setError('Impossibile contattare il server.')
    } finally {
      setSending(false)
    }
  }

  const confirmRecap = useMemo(() => {
    const pushPart = `${recipients.pushCount ?? '?'} utenti con notifiche attive`
    const emailPart = sendEmail
      ? ` e ${recipients.emailCount ?? '?'} indirizzi email`
      : ''
    return `La notifica verrà inviata a ${pushPart}${emailPart}. Vuoi procedere?`
  }, [recipients, sendEmail])

  return (
    <div className="broadcast-page">
      <div className="column">
        <TitleCard
          title="Comunicazione a tutti gli utenti"
          subtitle="Invia una notifica push e, opzionalmente, una mail a tutti gli utenti della piattaforma."
        />
      </div>

      <div className="broadcast-grid">
        <div className="broadcast-card">
          <h2>Notifica push</h2>
          <p className="broadcast-help">
            Apparirà sul telefono e sul desktop degli utenti che hanno attivato
            le notifiche dalle proprie impostazioni.
          </p>

          <div className="broadcast-stats">
            <div className="broadcast-stat">
              <span className="num">
                {recipients.pushCount === null ? '—' : recipients.pushCount}
              </span>
              <span className="label">Destinatari notifica push</span>
            </div>
            {sendEmail && (
              <div className="broadcast-stat">
                <span className="num">
                  {recipients.emailCount === null ? '—' : recipients.emailCount}
                </span>
                <span className="label">Destinatari email</span>
              </div>
            )}
          </div>

          {!recipients.pushConfigured && (
            <div className="broadcast-warning">
              Le notifiche push non sono configurate sul server (manca VAPID).
              Le push non verranno inviate finché non lo configuri.
            </div>
          )}

          <div className="modal-field">
            <label className="modal-label">
              Titolo notifica
              <span className="modal-counter">
                {pushTitle.length}/{TITLE_MAX}
              </span>
            </label>
            <input
              className="modal-input"
              type="text"
              value={pushTitle}
              maxLength={TITLE_MAX}
              placeholder="Es: Menu speciale di domani"
              onChange={e => setPushTitle(e.target.value)}
              disabled={sending}
            />
          </div>
          <div className="modal-field">
            <label className="modal-label">
              Messaggio
              <span className="modal-counter">
                {pushBody.length}/{BODY_MAX}
              </span>
            </label>
            <textarea
              className="modal-textarea"
              rows={3}
              value={pushBody}
              maxLength={BODY_MAX}
              placeholder="Es: Domani trovi una sorpresa nel menu! Apri l'app per ordinare."
              onChange={e => setPushBody(e.target.value)}
              disabled={sending}
            />
            <p className="modal-help">
              Tieni il messaggio breve: i sistemi operativi tagliano dopo poche
              righe.
            </p>
          </div>

          <div className="broadcast-toggle-row">
            <div className="broadcast-toggle-label">
              Invia anche per email
              <span className="broadcast-toggle-sub">
                Mail HTML inviata da cutcatering@soolutions.io. Le risposte
                arrivano sulla mail dell'admin.
              </span>
            </div>
            <label className="broadcast-switch">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
                disabled={sending}
              />
              <span className="slider"></span>
            </label>
          </div>

          {sendEmail && !recipients.emailConfigured && (
            <div className="broadcast-warning">
              L'invio email non è configurato sul server (mancano le credenziali
              AWS SES). Le email non verranno inviate finché non lo configuri.
            </div>
          )}

          {sendEmail && (
            <>
              <div className="modal-field">
                <label className="modal-label">
                  Oggetto email
                  <span className="modal-counter">
                    {emailSubject.length}/{EMAIL_SUBJECT_MAX}
                  </span>
                </label>
                <input
                  className="modal-input"
                  type="text"
                  value={emailSubject}
                  maxLength={EMAIL_SUBJECT_MAX}
                  placeholder="Es: Una piccola sorpresa nel menu di domani"
                  onChange={e => setEmailSubject(e.target.value)}
                  disabled={sending}
                />
              </div>
              <div className="modal-field">
                <label className="modal-label">
                  Corpo email
                  <span className="modal-counter">
                    {emailBody.length}/{EMAIL_BODY_MAX}
                  </span>
                </label>
                <textarea
                  className="modal-textarea"
                  rows={8}
                  value={emailBody}
                  maxLength={EMAIL_BODY_MAX}
                  placeholder="Scrivi il messaggio. Lascia una riga vuota per separare i paragrafi."
                  onChange={e => setEmailBody(e.target.value)}
                  disabled={sending}
                />
                <p className="modal-help">
                  Solo testo: a capo singolo va a capo, due a capo creano un
                  nuovo paragrafo.
                </p>
              </div>
            </>
          )}

          {error && <div className="broadcast-error">{error}</div>}

          <div className="broadcast-actions">
            <button
              type="button"
              className="broadcast-action-primary"
              onClick={onClickInvia}
              disabled={sending || !canSubmit}
            >
              {sending ? 'Invio in corso…' : 'Invia broadcast'}
            </button>
          </div>
        </div>

        <div className="broadcast-card">
          <h2>Anteprima</h2>
          <p className="broadcast-help">
            Così appariranno notifica e mail. Ricontrolla prima di inviare!
          </p>
          <PushPreview title={pushTitle} body={pushBody} />
          {sendEmail && (
            <EmailPreview
              subject={emailSubject}
              body={emailBody}
              fromName="CUT Catering"
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Confermare l'invio?"
        message={confirmRecap}
        confirmLabel={sendEmail ? 'Invia push + email' : 'Invia push'}
        cancelLabel="Torna indietro"
        onConfirm={onConfirmSend}
        onCancel={() => setConfirmOpen(false)}
      />

      <AlertModal
        open={!!resultAlert}
        title={resultAlert ? resultAlert.title : ''}
        message={resultAlert ? resultAlert.message : ''}
        onClose={() => setResultAlert(null)}
      />
    </div>
  )
}

export default Broadcast
